import React, { useState } from 'react';
import { Search, MapPin, Truck, CheckCircle, XCircle, Package, Sparkles, AlertCircle } from 'lucide-react';
import { useMyContext } from '../../../../context/MyContext';

import NavBar from '../../../../component/b2c/navBar/navBar';
import Sidebar from '../../../../component/b2c/sidebar/sidebar';
import { Base_Url, getpincode } from '../../../../config/config';
import { toast } from 'react-toastify';

const PincodeService = () => {
  const [pincode, setPincode] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const { isDarkMode, isToggleSidebar } = useMyContext();
  const [pincodeData, setPincodeData] = useState([]);
  const [showResults, setShowResults] = useState(false);

  const handleCheck = async () => {
    setIsChecking(true);
    setShowResults(false);
    const token = localStorage.getItem('token');
    const cus_id = localStorage.getItem('cus_id');
    const urlgetPincode = `${Base_Url}${getpincode}?pincode=${pincode}`;

    try {
      const response = await fetch(urlgetPincode, {
        method: 'GET',
        headers: {
          'token': token,
          'cus_id': cus_id,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();

      if (data && data.status === "SUCCESS") {
        setPincodeData(data.data);
        setIsChecking(false);
        setShowResults(true);
      } else if (data && data.message === "Unauthorized access - Invalid token") {
        toast.error(data.message + " Please login Again");
        setIsChecking(false);
      } else if (data && data.status === "FAIL") {
        toast.error("Something Went Wrong Please Try Again With Another Pincode");
        setIsChecking(false);
      } else {
        toast.error("Failed to fetch city and state data");
        setIsChecking(false);
      }
    } catch (error) {
      console.error('Error fetching pincode data:', error);
      toast.error("Error fetching pincode data");
      setIsChecking(false);
    }
  };

  return (
    <div className={`h-screen flex flex-col overflow-hidden ${isDarkMode ? 'dark:bg-darkmodeColor' : 'bg-white'}`}>
      <div className="z-10">
        <NavBar />
      </div>
      <div className="flex flex-1 overflow-hidden">
        {isToggleSidebar && (
          <div
            className={`fixed z-20 h-full w-64 md:relative duration-300 bg-gray-200 dark:bg-darkmodeColor dark:text-white md:bg-transparent mt-4 md:w-64 md:z-20`}
            style={
              window.innerWidth < 768
                ? { top: "113px", height: "calc(100vh - 115px)", overflowY: 'auto' }
                : {}
            }>
            <Sidebar />
          </div>
        )}

        <div className='shadow-custom-light dark:shadow-custom-dark custom-scrollbar overflow-y-auto rounded-custom w-full mt-3 mb-3 mr-3 ml-4'>
          <div className={`p-4 md:p-6 custom-scrollbar overflow-y-auto dark:bg-darkmodeColor ${isToggleSidebar ? 'ml-0' : 'mr-0'} ${isToggleSidebar ? 'w-full' : 'w-full'}`}>

            {/* Header Section */}
            <div className="text-center mb-8 animate-fade-in">
              <div className="inline-block p-4 bg-customPurple/10 rounded-full mb-4">
                <Package className="w-12 h-12 text-customPurple animate-bounce" />
              </div>
              <h1 className="text-2xl md:text-5xl font-bold bg-customPurple bg-clip-text text-transparent mb-3">
                Pincode Service Checker
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-300">
                Discover delivery availability in your area instantly
              </p>
            </div>

            {/* Main Content Grid */}
            <div className={`grid grid-cols-1 ${showResults && pincodeData.length > 0 ? 'lg:grid-cols-2' : 'lg:grid-cols-1'} gap-6 items-start max-w-7xl mx-auto`}>

              {/* Left Side - Search Card */}
              <div className="bg-white dark:bg-darkmodeColor rounded-2xl shadow-md overflow-hidden transition-all">

                {/* Header */}
                <div className="bg-customPurple p-2">
                  <div className="flex items-center">
                    <div className="w-6 h-6 bg-white/20 rounded-xl flex items-center justify-center mr-3">
                      <MapPin className="w-3 h-3 text-white" />
                    </div>
                    <div>
                      <h2 className="text-sm font-semibold text-white">
                        Check Availability
                      </h2>
                      <p className="text-xs text-purple-100">
                        Enter your pincode
                      </p>
                    </div>
                  </div>
                </div>

                {/* Body */}
                <div className="p-4">
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-2">
                    Your Pincode
                  </label>

                  <div className="relative mb-4">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MapPin className="h-4 w-4 text-customPurple" />
                    </div>

                    <input
                      type="text"
                      maxLength="6"
                      className="w-full pl-10 pr-3 py-2.5 text-xs bg-gray-50 dark:bg-darkmodeColor border border-gray-300 rounded-xl focus:border-customPurple focus:ring-2 focus:ring-customPurple/30"
                      placeholder="6-digit pincode"
                      value={pincode}
                      onChange={(e) => setPincode(e.target.value.replace(/\D/g, ''))}
                    />
                  </div>

                  {/* Progress */}
                  <div className="flex justify-between items-center mb-4">
                    <p className="text-[11px] text-gray-500 dark:text-white">
                      {pincode.length}/6 digits
                    </p>
                    <div className="flex space-x-1">
                      {[...Array(6)].map((_, i) => (
                        <div
                          key={i}
                          className={`w-2 h-2 rounded-full ${i < pincode.length ? 'bg-customPurple' : 'bg-gray-300'
                            }`}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Button */}
                  <button
                    onClick={handleCheck}
                    disabled={isChecking || pincode.length !== 6}
                    className={`w-full py-2.5 rounded-xl text-xs font-semibold text-white transition ${isChecking || pincode.length !== 6
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-customPurple hover:opacity-90'
                      }`}
                  >
                    {isChecking ? 'Checking...' : 'Check Availability'}
                  </button>

                  {/* Info */}
                  {/* <div className="grid grid-cols-2 gap-3 mt-4">
                    <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                      <Truck className="w-5 h-5 text-customPurple mb-1" />
                      <p className="text-[11px] font-semibold">Multiple Couriers</p>
                    </div>
                    <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                      <CheckCircle className="w-5 h-5 text-green-600 mb-1" />
                      <p className="text-[11px] font-semibold">Instant Check</p>
                    </div>
                  </div> */}
                </div>
              </div>


              {/* Right Side - Results Table */}
              {showResults && pincodeData && pincodeData.length > 0 && (
                <div className="bg-white dark:bg-darkmodeColor rounded-3xl shadow-custom-light dark:shadow-custom-dark overflow-hidden animate-slide-in-right">
                  {/* Table Header */}
                  <div className="bg-customPurple p-5">
                    <h3 className="text-sm font-bold text-white flex items-center">
                      <CheckCircle className="w-4 h-4 mr-3 animate-bounce" />
                      Service Available
                    </h3>
                    {/* <p className="text-purple-100 mt-1">Delivery partners for your area</p> */}
                  </div>

                  {/* Table Content */}
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gradient-to-r from-purple-100 to-blue-100 dark:from-purple-900/30 dark:to-blue-900/30">
                        <tr>
                          <th className="p-6 text-left text-sm font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                            <div className="flex items-center">
                              <MapPin className="w-4 h-4 mr-2 text-customPurple" />
                              Pincode
                            </div>
                          </th>
                          <th className="p-6 text-left text-sm font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                            <div className="flex items-center">
                              <Package className="w-4 h-4 mr-2 text-customPurple" />
                              City
                            </div>
                          </th>
                          <th className="p-6 text-left text-sm font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                            State
                          </th>
                          <th className="p-6 text-left text-sm font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                            <div className="flex items-center">
                              <Truck className="w-4 h-4 mr-2 text-customPurple" />
                              Partners
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {pincodeData.map((shipment, index) => {
                          const partners = [];
                          if (shipment.delhivery === "1") partners.push({ name: "Delhivery", color: "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300" });
                          if (shipment.shiprocket === "1") partners.push({ name: "Shiprocket", color: "bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300" });
                          if (shipment.ecom === "1") partners.push({ name: "Ecom", color: "bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300" });
                          if (shipment.ekart === "1") partners.push({ name: "Ekart", color: "bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300" });
                          if (shipment.xpressbees === "1") partners.push({ name: "XpressBees", color: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300" });

                          return (
                            <tr
                              key={index}
                              className="hover:bg-purple-50 dark:hover:bg-purple-900/10 transition-all duration-300 animate-fade-in"
                              style={{ animationDelay: `${index * 100}ms` }}
                            >
                              <td className="p-4  pl-6 text-base font-bold text-customPurple">
                                {shipment.pincode}
                              </td>
                              <td className="p-4 pl-6 text-sm font-semibold text-gray-800 dark:text-gray-200">
                                {shipment.city}
                              </td>
                              <td className="p-4  pl-6 text-sm text-gray-700 dark:text-gray-300">
                                {shipment.state}
                              </td>
                              <td className="p-4 pl-6">
                                {partners.length > 0 ? (
                                  <div className="flex flex-wrap gap-2">
                                    {partners.map((partner, idx) => (
                                      <span
                                        key={idx}
                                        className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all duration-300 hover:scale-110 ${partner.color}`}
                                      >
                                        ✓ {partner.name}
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="flex items-center text-red-500 font-medium">
                                    <XCircle className="w-4 h-4 mr-1" />
                                    No Partner
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>  
            {/* No Results State */}
            {showResults && (!pincodeData || pincodeData.length === 0) && (
              <div className="max-w-2xl mx-auto mt-8 p-8 bg-gradient-to-r from-red-500 to-pink-600 rounded-3xl text-white text-center animate-fade-in shadow-2xl">
                <AlertCircle className="w-16 h-16 mx-auto mb-4 animate-bounce" />
                <h3 className="text-3xl font-bold mb-3">No Service Available</h3>
                <p className="text-lg text-red-100">Sorry, we don't have delivery partners for this pincode yet.</p>
                <p className="text-sm text-red-200 mt-2">Please try another pincode or check back later.</p>
              </div>
            )}
          </div>
        </div>
      </div>
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slide-in-right {
          from { opacity: 0; transform: translateX(30px); }
          to { opacity: 1; transform: translateX(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.6s ease-out;
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.6s ease-out;
        }
      `}</style>
    </div>
  );
};

export default PincodeService;